import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Heart, Award } from 'lucide-react';
import { useSiteImagesContext } from '@/contexts/SiteImagesContext';

const AboutPage = () => {
  const { images: siteImages } = useSiteImagesContext();
  // Team roster mirrors the org structure — CEO, Operational, Office —
  // per the client's revision brief. Each member's slot key follows the
  // pattern `team.<first-name-lowercase>` — must stay in sync with the
  // backend site_images_registry.py.
  const teamGroups = [
    {
      title: 'Leadership',
      members: [
        { name: 'Andini Haryani', role: 'CEO', slot: 'team.andini-haryani' },
      ],
    },
    {
      title: 'Operational Team',
      members: [
        { name: 'Vinnie', role: 'Operations', slot: 'team.vinnie' },
        { name: 'Citra', role: 'Operations', slot: 'team.citra' },
        { name: 'Azur', role: 'Operations', slot: 'team.azur' },
      ],
    },
    {
      title: 'Office Team',
      members: [
        { name: 'Dedy', role: 'Office', slot: 'team.dedy' },
        { name: 'Irsah', role: 'Office', slot: 'team.irsah' },
        { name: 'Priscila', role: 'Office', slot: 'team.priscila' },
        { name: 'Virda', role: 'Office', slot: 'team.virda' },
      ],
    },
  ];

  const values = [
    {
      icon: Users,
      title: 'Local Expertise',
      description: 'Our team is rooted in Bali. We understand the culture, the market, and the unique challenges of property ownership here.',
    },
    {
      icon: Target,
      title: 'Boutique Approach',
      description: 'We limit the number of properties we manage to ensure every villa gets the attention it deserves.',
    },
    {
      icon: Heart,
      title: 'Guest-Centric',
      description: 'Happy guests mean great reviews, repeat bookings, and better returns for property owners.',
    },
    {
      icon: Award,
      title: 'Transparency',
      description: 'Clear communication, honest pricing, and detailed reporting. No hidden fees, no surprises.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="bg-warm-sand py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-charcoal mb-6">
                Your Local Partners in<br />
                <span className="italic text-ubud-jungle">Bali Property</span>
              </h1>
              <p className="font-body text-lg text-charcoal-light mb-8 leading-relaxed">
                Lalita Properties was founded with a simple mission: to help villa owners succeed in Bali's dynamic property market while delivering exceptional experiences to guests.
              </p>
              <p className="font-body text-charcoal-light leading-relaxed">
                We combine local knowledge with international standards, offering a boutique approach to property management and real estate that larger agencies can't match.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <img
                src="https://res.cloudinary.com/dsbucdyd5/image/upload/v1779850786/lalita-stays/lalita-stays/villa-rhea-at-dhara-canggu-0.jpg"
                alt="Villa Rhea at Dhara, Canggu — Lalita Properties"
                className="rounded-3xl shadow-floating w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-6">
              Our Story
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none font-body text-charcoal-light"
          >
            <p>
              Lalita Properties was founded to fill a gap in the Bali property market. Foreign investors were buying beautiful villas but struggling to manage them from afar. Local management companies were either too large to provide personal service or too informal to deliver consistent results.
            </p>
            <p>
              We set out to create something different—a property management company that treats every villa as if it were our own, while providing the professional systems and reporting that investors need.
            </p>
            <p>
              Over the years, we've grown to manage properties across Bali's most sought-after locations. We've helped dozens of owners buy and sell properties, and we've hosted thousands of guests from around the world.
            </p>
            <p>
              But we've never lost sight of our boutique roots. We still believe in knowing every owner by name, understanding each property's unique character, and going above and beyond for every guest.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-warm-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
              Our Values
            </h2>
            <p className="font-body text-lg text-charcoal-light max-w-2xl mx-auto">
              These principles guide everything we do at Lalita Properties.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-card"
              >
                <value.icon className="w-10 h-10 text-ubud-jungle mb-4" />
                <h3 className="font-heading text-xl font-medium text-charcoal mb-2">
                  {value.title}
                </h3>
                <p className="font-body text-charcoal-light">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal mb-4">
              Meet Our Team
            </h2>
            <p className="font-body text-lg text-charcoal-light max-w-2xl mx-auto">
              A dedicated team of local and international professionals.
            </p>
          </motion.div>

          <div className="space-y-14" data-testid="team-groups">
            {teamGroups.map((group) => (
              <div key={group.title}>
                <h3 className="font-heading text-xl text-ubud-jungle mb-6 uppercase tracking-[0.2em] text-center">
                  {group.title}
                </h3>
                <div className={`grid gap-8 justify-center ${
                  group.members.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' :
                  group.members.length <= 3 ? 'grid-cols-1 sm:grid-cols-3 max-w-3xl mx-auto' :
                  'grid-cols-2 md:grid-cols-4 max-w-4xl mx-auto'
                }`}>
                  {group.members.map((member, idx) => {
                    const portraitUrl = siteImages[member.slot]?.is_custom ? siteImages[member.slot].url : '';
                    return (
                      <motion.div
                        key={member.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.08 }}
                        className="text-center"
                      >
                        <div className="w-28 h-28 bg-warm-sand rounded-full mx-auto mb-3 overflow-hidden flex items-center justify-center">
                          {portraitUrl ? (
                            <img src={portraitUrl} alt={member.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-heading text-2xl text-ubud-jungle">
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          )}
                        </div>
                        <h4 className="font-heading text-lg font-medium text-charcoal">
                          {member.name}
                        </h4>
                        <p className="text-ubud-jungle text-xs uppercase tracking-wider">{member.role}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-charcoal">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
              Ready to work with us?
            </h2>
            <p className="font-body text-white/70 text-lg mb-8">
              Whether you're looking to buy, sell, or have your villa managed, we'd love to hear from you.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-charcoal hover:bg-warm-sand transition-all duration-300 rounded-full px-8 py-4 font-body font-medium"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
